import React, { useState } from "react";
import {
  Typography,
  Table,
  Space,
  DatePicker,
  Button,
  Statistic,
  Row,
  Col,
  Input,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import {
  useReportsServiceDownloadPurchaseReport,
  useReportsServiceGetPurchaseReport,
} from "api/queries";

const { Title } = Typography;

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const PurchaseSummary: React.FC = () => {
  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");
  const [search, setSearch] = useState("");
  const { data, isLoading, refetch } = useReportsServiceGetPurchaseReport({
    limit: 10,
    offset: 0,
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
    search,
  });
  const { mutate: downloadPurchaseSummaryReport, isPending: isDownloading } =
    useReportsServiceDownloadPurchaseReport();

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
    refetch();
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
    refetch();
  };

  const handleDownload = () => {
    downloadPurchaseSummaryReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        search,
      },
      {
        onSuccess: (response: { downloadUrl?: string }) => {
          if (response?.downloadUrl) {
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "purchase_summary.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  // KPIs
  type KPIs = {
    totalItems?: number;
    totalVendors?: number;
    totalValue?: number;
    totalEstimatedRevenue?: number;
  };
  const kpis: KPIs = data?.kpis || {};
  const tableData = (data?.items || []).map((item: any) => ({
    ...item,
    key: item.purchaseOrderItemId,
  }));

  const columns = [
    { title: "Order #", dataIndex: "purchaseOrderNumber", key: "purchaseOrderNumber" },
    { title: "Order Date", dataIndex: "purchaseOrderDate", key: "purchaseOrderDate", render: (date: string) => dayjs(date).format("YYYY-MM-DD") },
    { title: "Vendor", dataIndex: "vendorName", key: "vendorName" },
    { title: "Product", dataIndex: "productName", key: "productName" },
    { title: "SKU", dataIndex: "productSku", key: "productSku" },
    { title: "Barcode", dataIndex: "productBarcode", key: "productBarcode" },
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Brand", dataIndex: "brand", key: "brand" },
    { title: "Buying Price", dataIndex: "buyingPrice", key: "buyingPrice" },
    { title: "Selling Price", dataIndex: "sellingPrice", key: "sellingPrice" },
    { title: "Quantity", dataIndex: "quantity", key: "quantity" },
    { title: "Estimated Revenue", dataIndex: "estimatedRevenue", key: "estimatedRevenue" },
  ];

  // Debounce search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    refetch();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.2 }}
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={2}>Purchase Summary</Title>
          <Space>
            <Input.Search
              allowClear
              placeholder="Search purchases..."
              style={{ width: 260 }}
              value={search}
              onChange={handleSearchChange}
            />
            <Select
              value={periodFilter}
              onChange={handlePeriodChange}
              style={{ width: 120 }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              style={{ width: 120 }}
              placeholder="From"
            />
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              style={{ width: 120 }}
              placeholder="To"
            />
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Space>
        </Space>
        <Row gutter={24} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="Total Items" value={kpis?.totalItems ?? 0} />
          </Col>
          <Col span={6}>
            <Statistic title="Total Vendors" value={kpis?.totalVendors ?? 0} />
          </Col>
          <Col span={6}>
            <Statistic title="Total Value" value={kpis?.totalValue ?? 0} />
          </Col>
          <Col span={6}>
            <Statistic title="Total Estimated Revenue" value={kpis?.totalEstimatedRevenue ?? 0} />
          </Col>
        </Row>
        <Table
          columns={columns}
          dataSource={tableData}
          loading={isLoading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          bordered
        />
      </Space>
    </motion.div>
  );
};

export default PurchaseSummary;
